import { NavItem } from './nav.model'

/**
 * The single source of truth for the sidebar.
 *
 * The menu this replaced was 31 flat rows in display order with no grouping:
 * `المدن` sat beside `المناطق`, but `المبيعات`, `التذاكر`, `تقرير المبيعات الشامل`
 * and `روابط الدفع` were scattered a hundred lines apart. Every destination is
 * still here and every route and permission key is unchanged — only the shape is
 * new: six sections, collapsed by default, so the resting menu is six rows.
 *
 * Nested groups (`المسافر`, `الطلبات`, `الاعدادات`…) are the sections slated to
 * become tabbed hub pages. Modelling them as subgroups now means the tree the
 * admin learns does not move when those hubs land — the subgroup simply becomes
 * a single link to the hub.
 *
 * `keywords` are additive only: they never affect what renders, they widen what
 * the command palette can match.
 */
export const NAV_ITEMS: NavItem[] = [
  {
    // No pageKey: the landing page is reachable by every signed-in admin, and its
    // contents gate themselves.
    id: 'dashboard',
    label: 'الرئيسية',
    route: '/dashboard',
    icon: 'dashboard',
    keywords: ['dashboard', 'home', 'لوحة التحكم'],
  },
  {
    id: 'sales-group',
    label: 'المبيعات والإيرادات',
    icon: 'fund',
    children: [
      {
        id: 'orders',
        label: 'المبيعات',
        route: '/orders',
        icon: 'ordered-list',
        pageKey: 'orders',
        keywords: ['الطلبات', 'orders', 'sales'],
      },
      {
        id: 'tickets',
        label: 'التذاكر',
        route: '/tickets',
        icon: 'snippets',
        pageKey: 'tickets',
        keywords: ['tickets'],
      },
      {
        id: 'revenue-report',
        label: 'تقرير المبيعات الشامل',
        route: '/revenue-report',
        icon: 'fund',
        pageKey: 'revenue-report',
        keywords: ['الايرادات', 'revenue', 'report'],
      },
      {
        id: 'payment-links',
        label: 'روابط الدفع',
        route: '/payment-links',
        icon: 'link',
        pageKey: 'payment-links',
        keywords: ['payment', 'links'],
      },
      {
        id: 'wallets',
        label: 'المحافظ',
        route: '/wallets',
        icon: 'account-book',
        pageKey: 'wallets',
        keywords: ['wallet', 'الرصيد'],
      },
    ],
  },
  {
    id: 'events-group',
    label: 'الفعاليات',
    icon: 'wallet',
    children: [
      {
        id: 'events',
        label: 'الفعاليات',
        route: '/events',
        icon: 'wallet',
        pageKey: 'events',
        keywords: ['الحفلات', 'events'],
      },
      {
        id: 'categories',
        label: 'تصنيف الفعاليات',
        route: '/categories',
        icon: 'database',
        pageKey: 'categories',
        keywords: ['الاقسام', 'categories'],
      },
      {
        id: 'events-sections',
        label: 'اقسام الفعاليات',
        route: '/events-sections',
        icon: 'partition',
        pageKey: 'events-sections',
        keywords: ['sections'],
      },
    ],
  },
  {
    id: 'entertainment-group',
    label: 'مراكز الترفيه والأنشطة',
    icon: 'play-circle',
    // No gate on the group: the menu this replaced checked only `entertainment ||
    // entertainment-alhokair`, which hid the whole section from an admin whose only
    // grant was `entertainment-purchased-tickets`. Letting the children decide fixes that.
    children: [
      {
        id: 'entertainment',
        label: 'مراكز الترفيه',
        route: '/entertainment',
        icon: 'play-circle',
        pageKey: 'entertainment',
        keywords: ['entertainment'],
      },
      {
        id: 'entertainment-alhokair',
        label: 'مركز ترفيه الحكير',
        route: '/entertainment-alhokair',
        icon: 'play-circle',
        pageKey: 'entertainment-alhokair',
        keywords: ['alhokair', 'الحكير'],
      },
      {
        id: 'entertainment-purchased-tickets',
        label: 'مركز ترفيه التذاكر المدفوعه',
        route: '/entertainment-purchased-tickets',
        icon: 'play-circle',
        pageKey: 'entertainment-purchased-tickets',
        keywords: ['purchased', 'tickets'],
      },
      {
        id: 'almosafer-group',
        label: 'المسافر',
        icon: 'ordered-list',
        // The children carry no key of their own — this one gate covers the section.
        pageKey: 'almosafer',
        children: [
          {
            id: 'almosafer-activities',
            label: 'الأنشطة',
            route: '/almosafer/activities',
            keywords: ['almosafer', 'activities'],
          },
          {
            id: 'almosafer-orders',
            label: 'المبيعات',
            route: '/almosafer/orders',
            keywords: ['almosafer', 'orders'],
          },
          {
            id: 'almosafer-categories',
            label: 'الاصناف',
            route: '/almosafer/categories',
            keywords: ['almosafer', 'categories'],
          },
          {
            id: 'almosafer-cities',
            label: 'المدن',
            route: '/almosafer/cities',
            keywords: ['almosafer', 'cities'],
          },
          {
            id: 'almosafer-icons',
            label: 'الايقونات',
            route: '/almosafer/icons',
            keywords: ['almosafer', 'icons'],
          },
        ],
      },
    ],
  },
  {
    id: 'partners-group',
    label: 'الشركاء والطلبات',
    icon: 'usergroup-add',
    children: [
      {
        id: 'ticket-providers',
        label: 'مزودي التذاكر',
        route: '/ticket-providers',
        icon: 'usergroup-add',
        pageKey: 'ticket-providers',
        keywords: ['المنظمين', 'الشركاء', 'providers', 'organizers'],
      },
      {
        id: 'requests-group',
        label: 'الطلبات',
        icon: 'ordered-list',
        pageKey: 'requests',
        children: [
          {
            id: 'requests-ticket-providers',
            label: 'مزودي التذاكر',
            route: '/requests/ticket-providers',
            keywords: ['طلبات', 'requests', 'providers'],
          },
          {
            id: 'requests-companies-reservations',
            label: 'حجوزات الشركات',
            route: '/requests/companies-reservations',
            keywords: ['طلبات', 'requests', 'companies', 'reservations'],
          },
          {
            id: 'requests-money-collection',
            label: 'طلبات التحصيل',
            route: '/requests/money-collection',
            keywords: ['طلبات', 'requests', 'money', 'collection'],
          },
        ],
      },
      {
        id: 'support-tickets',
        label: 'تذاكر الدعم',
        route: '/support-tickets',
        icon: 'customer-service',
        pageKey: 'support-tickets',
        keywords: ['الدعم', 'support'],
      },
      {
        // Previously reachable only via a button on the support-tickets list.
        id: 'support-ticket-categories',
        label: 'تصنيفات تذاكر الدعم',
        route: '/support-tickets/categories',
        icon: 'appstore',
        pageKey: 'support-tickets',
        keywords: ['الدعم', 'support', 'categories', 'تصنيفات'],
      },
    ],
  },
  {
    id: 'customers-group',
    label: 'العملاء',
    icon: 'user',
    children: [
      {
        id: 'users',
        label: 'المستخدمين',
        route: '/users',
        icon: 'user',
        pageKey: 'users',
        keywords: ['العملاء', 'الزبائن', 'users', 'customers'],
      },
      {
        id: 'tiers',
        label: 'مستويات العملاء',
        route: '/tiers',
        icon: 'trophy',
        pageKey: 'tiers',
        keywords: ['الولاء', 'tiers', 'loyalty'],
      },
      {
        id: 'promotions-group',
        label: 'العروض والحملات',
        icon: 'percentage',
        children: [
          {
            id: 'coupons',
            label: 'الخصومات',
            route: '/coupons',
            icon: 'percentage',
            pageKey: 'coupons',
            keywords: ['الكوبونات', 'coupons', 'discounts'],
          },
          {
            id: 'spin-wheel',
            label: 'عجلة الحظ',
            route: '/spin-wheel',
            icon: 'trophy',
            pageKey: 'spin-wheel',
            keywords: ['spin', 'wheel'],
          },
          {
            // Had no menu entry at all — reachable only by typing the URL.
            id: 'spin-wheel-manual-fulfillment',
            label: 'الجوائز اليدوية (تذاكر المسافر)',
            route: '/spin-wheel/manual-fulfillment',
            icon: 'gift',
            pageKey: 'spin-wheel',
            keywords: ['spin', 'wheel', 'manual', 'fulfillment', 'الجوائز'],
          },
          {
            id: 'games',
            label: 'العب واربح',
            route: '/games',
            icon: 'rocket',
            pageKey: 'games',
            keywords: ['games', 'play', 'win', 'quiz', 'leaderboard', 'rewards', 'الألعاب', 'لوحة الصدارة', 'النقاط'],
          },
          {
            id: 'games-redemptions',
            label: 'جوائز العب واربح',
            route: '/games/redemptions',
            icon: 'gift',
            pageKey: 'games',
            keywords: ['games', 'rewards', 'redemptions', 'الجوائز', 'الاستبدال'],
          },
        ],
      },
      {
        id: 'notifications-group',
        label: 'الإشعارات',
        icon: 'notification',
        route: '/notifications',
        children: [
          {
            id: 'notification',
            label: 'الإشعارات',
            route: '/notifications/list',
            icon: 'notification',
            pageKey: 'notification',
            keywords: ['notifications', 'push'],
          },
          {
            id: 'notification-categories',
            label: 'تصنيفات الإشعارات',
            route: '/notifications/categories',
            icon: 'tags',
            pageKey: 'notification-categories',
            keywords: ['notifications', 'categories'],
          },
        ],
      },
    ],
  },
  {
    // A section admins go to directly, so it sits at the top level rather than two
    // clicks deep. It carries a `route`, so the sidebar renders it as a single link
    // into the tabbed hub instead of an expandable submenu.
    id: 'marketing-group',
    label: 'التسويق',
    icon: 'bar-chart',
    route: '/marketing',
    children: [
      {
        id: 'marketing-team',
        label: 'فريق التسويق',
        route: '/marketing/team',
        icon: 'bar-chart',
        pageKey: 'marketing-team',
        keywords: ['marketing', 'team'],
      },
      {
        id: 'marketing-agency',
        label: 'وكالة التسويق',
        route: '/marketing/agency',
        icon: 'shop',
        pageKey: 'marketing-agency',
        keywords: ['marketing', 'agency'],
      },
      {
        id: 'newsletter',
        label: 'النشرة البريدية',
        route: '/marketing/newsletter',
        icon: 'read',
        pageKey: 'newsletter',
        keywords: ['newsletter', 'email'],
      },
    ],
  },
  {
    // Same shape as the marketing group: it carries a `route`, so the sidebar shows
    // one link into the tabbed hub and the children become its tabs.
    id: 'seo-group',
    label: 'تحسين محركات البحث',
    icon: 'global',
    route: '/seo',
    pageKey: 'seo',
    children: [
      {
        id: 'seo-overview',
        label: 'نظرة عامة',
        route: '/seo/overview',
        icon: 'global',
        pageKey: 'seo',
        keywords: ['seo', 'overview', 'تحسين'],
      },
      {
        id: 'seo-suggestions',
        label: 'المقترحات',
        route: '/seo/suggestions',
        icon: 'bulb',
        pageKey: 'seo',
        keywords: ['seo', 'suggestions', 'مقترحات'],
      },
      {
        id: 'seo-runs',
        label: 'التشغيلات',
        route: '/seo/runs',
        icon: 'play-circle',
        pageKey: 'seo',
        keywords: ['seo', 'runs', 'تشغيلات'],
      },
      {
        id: 'seo-reports',
        label: 'التقارير والدروس',
        route: '/seo/reports',
        icon: 'read',
        pageKey: 'seo',
        keywords: ['seo', 'reports', 'lessons', 'تقارير'],
      },
      {
        id: 'seo-settings',
        label: 'الإعدادات',
        route: '/seo/settings',
        icon: 'setting',
        pageKey: 'seo',
        keywords: ['seo', 'settings', 'اعدادات'],
      },
    ],
  },
  {
    id: 'content-group',
    label: 'المحتوى والإعدادات',
    icon: 'setting',
    children: [
      {
        id: 'banners',
        label: 'البانرز',
        route: '/banners',
        icon: 'picture',
        pageKey: 'banners',
        keywords: ['banners', 'الاعلانات'],
      },
      {
        id: 'geo-group',
        label: 'البيانات الجغرافية',
        icon: 'global',
        route: '/geo',
        children: [
          {
            id: 'cities',
            label: 'المدن',
            route: '/geo/cities',
            icon: 'build',
            pageKey: 'cities',
            keywords: ['cities'],
          },
          {
            id: 'provinces',
            label: 'المناطق',
            route: '/geo/provinces',
            icon: 'build',
            pageKey: 'provinces',
            keywords: ['provinces', 'regions'],
          },
          {
            id: 'locations',
            label: 'المواقع',
            route: '/geo/locations',
            icon: 'global',
            pageKey: 'locations',
            keywords: ['الاماكن', 'locations', 'venues'],
          },
        ],
      },
      {
        id: 'media-group',
        label: 'الوسائط',
        icon: 'picture',
        children: [
          {
            id: 'informative-icons',
            label: 'ايقونات اعلامية',
            route: '/informative-icons',
            icon: 'file-image',
            pageKey: 'informative-icons',
            keywords: ['icons'],
          },
          {
            id: 'image-uploader',
            label: 'رفع الصور',
            route: '/image-uploader',
            icon: 'cloud-upload',
            pageKey: 'image-uploader',
            keywords: ['upload', 'images', 'الصور'],
          },
        ],
      },
      {
        id: 'settings-group',
        label: 'الاعدادات',
        icon: 'setting',
        route: '/settings',
        children: [
          {
            id: 'service-fees',
            label: 'رسوم الخدمة',
            route: '/settings/service-fees',
            icon: 'dollar',
            pageKey: 'service-fees',
            keywords: ['service', 'fees', 'الرسوم'],
          },
          {
            id: 'web-pages',
            label: 'الصفحات',
            route: '/settings/web-pages',
            icon: 'read',
            pageKey: 'web-pages',
            keywords: ['الاعدادات', 'pages'],
          },
          {
            id: 'access-control',
            label: 'التحكم في الوصول',
            route: '/settings/access-control',
            icon: 'setting',
            pageKey: 'access-control',
            keywords: ['الاعدادات', 'الصلاحيات', 'permissions', 'access'],
          },
          {
            id: 'admins',
            label: 'الفريق',
            route: '/settings/admins',
            icon: 'usergroup-add',
            pageKey: 'admins',
            keywords: ['الاعدادات', 'admins', 'team'],
          },
        ],
      },
    ],
  },
]
